<template>
  <div>
    <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 12px;">
      <h2 class="mb-0">Relatório de Solicitação de Orçamento Detalhado</h2>
      <v-btn outlined color="primary" @click="$router.push('/relatorios#comercial')">
        <v-icon left>mdi-arrow-left</v-icon>
        Voltar ao menu
      </v-btn>
    </div>

    <v-card class="mt-4">
      <v-card-text>
        <div class="d-flex flex-wrap align-center mb-4" style="gap: 12px;">
          <v-text-field
            v-model="filtros.dataInicio"
            type="date"
            label="Data início"
            solo
            hide-details
            class="c-filtro"
          />

          <v-text-field
            v-model="filtros.dataFim"
            type="date"
            label="Data fim"
            solo
            hide-details
            class="c-filtro"
          />

          <v-autocomplete
            v-model="filtros.tipoEquipamento"
            :items="opcoesTipoEquipamento"
            item-text="text"
            item-value="value"
            label="Tipo de Equipamento"
            solo
            hide-details
            clearable
            class="c-filtro"
          />

          <v-autocomplete
            v-model="filtros.cliente"
            :items="opcoesCliente"
            label="Cliente"
            solo
            hide-details
            clearable
            class="c-filtro"
          />

          <v-autocomplete
            v-model="filtros.vendedor"
            :items="opcoesVendedor"
            label="Vendedor"
            solo
            hide-details
            clearable
            class="c-filtro"
          />

          <v-autocomplete
            v-model="filtros.orcamentista"
            :items="opcoesOrcamentista"
            label="Orçamentista"
            solo
            hide-details
            clearable
            class="c-filtro"
          />

          <v-autocomplete
            v-model="filtros.status"
            :items="opcoesStatus"
            label="Status"
            solo
            hide-details
            clearable
            class="c-filtro"
          />

          <v-btn color="primary" :loading="loading" @click="buscarDados">
            <v-icon left>mdi-refresh</v-icon>
            Recarregar
          </v-btn>

          <v-menu>
            <template v-slot:activator="{ on }">
              <v-btn color="primary" outlined v-on="on" :disabled="loading || dadosOrdenados.length === 0">
                <v-icon left>mdi-file-export-outline</v-icon>
                Exportar
              </v-btn>
            </template>
            <v-list>
              <v-list-item link @click="exportarPdfDetalhado">
                <v-list-item-title>Exportar PDF</v-list-item-title>
              </v-list-item>
              <v-list-item link @click="exportarExcelDetalhado">
                <v-list-item-title>Exportar Excel</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>

        <v-data-table
          :headers="headers"
          :items="dadosFiltrados"
          :loading="loading"
          :options.sync="tableOptions"
          class="elevation-1"
          must-sort
          sort-by="dataSolicitacao"
          :sort-desc="true"
        >
          <template v-slot:item.dataSolicitacao="{ item }">
            {{ formatarDataPtBr(item.dataSolicitacao) }}
          </template>

          <template v-slot:item.precoComIcmsIpi="{ item }">
            {{ formatarMoedaPtBr(item.precoComIcmsIpi) }}
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import Vue from "vue";
import moment from "moment";
import * as XLSX from "xlsx";
import relatorioPermissionMixin from "@/mixins/relatorioPermissionMixin";
import EntityService from "@/services/entityService";
import {
  exportarPdfTabela,
  formatarDataPtBr,
  formatarMoedaPtBr,
  normalizarTexto
} from "@/views/Relatorios/base/relatorioBaseUtils";

const toDateKey = (valor = "") => {
  const texto = String(valor || "").trim();
  if (!texto) return null;

  const matchIsoComHora = texto.match(/^(\d{4})-(\d{2})-(\d{2})T/);
  if (matchIsoComHora) {
    const [, ano, mes, dia] = matchIsoComHora;
    return Date.UTC(Number(ano), Number(mes) - 1, Number(dia));
  }

  const matchIso = texto.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (matchIso) {
    const [, ano, mes, dia] = matchIso;
    return Date.UTC(Number(ano), Number(mes) - 1, Number(dia));
  }

  const data = new Date(texto);
  if (Number.isNaN(data.getTime())) return null;
  return Date.UTC(data.getFullYear(), data.getMonth(), data.getDate());
};

const toNumber = (valor) => {
  if (valor == null || valor === "") return 0;
  if (typeof valor === "number") return Number.isFinite(valor) ? valor : 0;

  const texto = String(valor).trim();
  if (!texto) return 0;

  const comVirgula = texto.includes(",");
  const normalizado = comVirgula
    ? texto.replace(/\./g, "").replace(",", ".")
    : texto;

  const numero = Number(normalizado);
  return Number.isFinite(numero) ? numero : 0;
};

// Faixa de marcas diacríticas combinantes (U+0300 a U+036F) expressa via código decimal
// (768-879) em vez de escape \u literal no fonte, para evitar qualquer problema de
// codificação de caracteres ao salvar este arquivo.
const REGEX_DIACRITICOS = new RegExp(`[${String.fromCharCode(768)}-${String.fromCharCode(879)}]`, "g");
const normalizarChave = (valor = "") => normalizarTexto(valor)
  .normalize("NFD")
  .replace(REGEX_DIACRITICOS, "")
  .toUpperCase();

export default {
  name: "RelatorioComercialSalesOrderDetalhadoView",
  mixins: [relatorioPermissionMixin],
  data() {
    return {
      relatorioSchemas: ["SalesOrder", "SolicitacaoDeOrcamento"],
      relatorioFiltrarPorOwner: false,
      loading: false,
      dados: [],
      tiposDeEquipamento: [],
      tipoEquipamentoService: new EntityService("TipoDeEquipamento"),
      filtros: {
        dataInicio: "",
        dataFim: "",
        tipoEquipamento: "",
        cliente: "",
        vendedor: "",
        orcamentista: "",
        status: ""
      },
      tableOptions: {
        sortBy: ["dataSolicitacao"],
        sortDesc: [true],
        page: 1,
        itemsPerPage: 10
      },
      headers: [
        { text: "Orçamento", value: "codigo" },
        { text: "Cliente", value: "clienteNome" },
        { text: "Projeto/Produto", value: "projetoProduto" },
        { text: "Data Solicitação", value: "dataSolicitacao" },
        { text: "Preço c/ ICMS e c/ IPI", value: "precoComIcmsIpi" },
        { text: "Status", value: "status" }
      ]
    };
  },
  computed: {
    opcoesTipoEquipamento() {
      return [...this.tiposDeEquipamento]
        .map((tipo) => {
          const sigla = String(tipo?.sigla || tipo?.codigo || tipo?.code || "").trim().toUpperCase();
          const descricao = tipo?.descricao || tipo?.nome || tipo?.description || tipo?.name || "";
          if (!sigla) return null;
          return { text: descricao ? `${sigla} - ${descricao}` : sigla, value: sigla };
        })
        .filter(Boolean)
        .sort((a, b) => a.value.localeCompare(b.value));
    },
    opcoesCliente() {
      return [...new Set(this.dados.map((item) => normalizarTexto(item.clienteNome)).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    },
    opcoesVendedor() {
      return [...new Set(this.dados.map((item) => normalizarTexto(item.vendedorNome)).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    },
    opcoesOrcamentista() {
      return [...new Set(this.dados.map((item) => normalizarTexto(item.orcamentistaNome)).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    },
    opcoesStatus() {
      return [...new Set(this.dados.map((item) => normalizarTexto(item.status)).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    },
    dadosFiltrados() {
      const inicio = this.filtros.dataInicio ? toDateKey(this.filtros.dataInicio) : null;
      const fim = this.filtros.dataFim ? toDateKey(this.filtros.dataFim) : null;

      const tipoEquipamento = String(this.filtros.tipoEquipamento || "").trim().toUpperCase();
      const cliente = normalizarTexto(this.filtros.cliente).toLowerCase();
      const vendedor = normalizarTexto(this.filtros.vendedor).toLowerCase();
      const orcamentista = normalizarTexto(this.filtros.orcamentista).toLowerCase();
      const status = normalizarTexto(this.filtros.status).toLowerCase();

      return this.dados.filter((item) => {
        const dataSolicitacao = toDateKey(item?.dataSolicitacao);
        const inicioOk = inicio == null || (dataSolicitacao != null && dataSolicitacao >= inicio);
        const fimOk = fim == null || (dataSolicitacao != null && dataSolicitacao <= fim);

        const tipoEquipamentoOk = !tipoEquipamento || String(item.tipoEquipamentoSigla || "").toUpperCase() === tipoEquipamento;
        const clienteOk = !cliente || normalizarTexto(item.clienteNome).toLowerCase().includes(cliente);
        const vendedorOk = !vendedor || normalizarTexto(item.vendedorNome).toLowerCase().includes(vendedor);
        const orcamentistaOk = !orcamentista || normalizarTexto(item.orcamentistaNome).toLowerCase().includes(orcamentista);
        const statusOk = !status || normalizarTexto(item.status).toLowerCase().includes(status);

        return inicioOk && fimOk && tipoEquipamentoOk && clienteOk && vendedorOk && orcamentistaOk && statusOk;
      });
    },
    dadosOrdenados() {
      const itens = [...this.dadosFiltrados];
      const sortBy = Array.isArray(this.tableOptions?.sortBy) ? this.tableOptions.sortBy : [];
      const sortDesc = Array.isArray(this.tableOptions?.sortDesc) ? this.tableOptions.sortDesc : [];

      if (!sortBy.length) return itens;

      const obterComparavel = (item, campo) => {
        const valor = item?.[campo];
        if (campo === "precoComIcmsIpi") return Number(valor || 0);
        if (campo === "dataSolicitacao") return toDateKey(valor) || 0;
        if (valor == null) return "";
        return String(valor).toLowerCase();
      };

      return itens.sort((a, b) => {
        for (let i = 0; i < sortBy.length; i += 1) {
          const campo = sortBy[i];
          const desc = Boolean(sortDesc[i]);
          const va = obterComparavel(a, campo);
          const vb = obterComparavel(b, campo);

          if (va < vb) return desc ? 1 : -1;
          if (va > vb) return desc ? -1 : 1;
        }

        return 0;
      });
    },
    linhasExportacao() {
      return this.dadosOrdenados.map((item) => ({
        ...item
      }));
    }
  },
  methods: {
    formatarDataPtBr,
    formatarMoedaPtBr,
    valorPareceGuid(valor = "") {
      return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(valor || "").trim());
    },
    obterDescricaoTipoEquipamento(dados = {}, item = {}) {
      const espelho = dados?._mirror_tipoDeEquipamento;
      const tipo = dados?.tipoDeEquipamento;
      const descricaoDireta =
        dados?.tipoDeEquipamentoDescricao ||
        tipo?.descricao ||
        tipo?.description ||
        tipo?.nome ||
        tipo?.name ||
        espelho?.descricao ||
        espelho?.description ||
        espelho?.nome ||
        espelho?.name ||
        item?.tipoDeEquipamentoDescricao;

      if (descricaoDireta) return descricaoDireta;

      const tipoId = typeof espelho === "string"
        ? espelho
        : typeof tipo === "string"
          ? tipo
          : tipo?.id || tipo?._id || espelho?.id || espelho?._id;
      const tipoCatalogo = this.tiposDeEquipamento.find((registro) => {
        const id = registro?.id || registro?._id;
        return id && tipoId && String(id).toLowerCase() === String(tipoId).toLowerCase();
      });
      const descricaoCatalogo = tipoCatalogo?.descricao || tipoCatalogo?.description || tipoCatalogo?.nome || tipoCatalogo?.name;

      if (descricaoCatalogo) return descricaoCatalogo;
      if (typeof espelho === "string" && !this.valorPareceGuid(espelho)) return espelho;
      return dados?.projetoDescricao || dados?.descricao || item?.projetoDescricao || item?.descricao || "-";
    },
    obterSiglaTipoEquipamento(dados = {}, item = {}) {
      const espelho = dados?._mirror_tipoDeEquipamento;
      const tipo = dados?.tipoDeEquipamento;
      const siglaDireta =
        dados?.tipoDeEquipamentoSigla ||
        tipo?.sigla ||
        tipo?.codigo ||
        tipo?.code ||
        espelho?.sigla ||
        espelho?.codigo ||
        espelho?.code ||
        item?.tipoDeEquipamentoSigla;

      if (siglaDireta) return String(siglaDireta).trim().toUpperCase();

      const tipoId = typeof espelho === "string"
        ? espelho
        : typeof tipo === "string"
          ? tipo
          : tipo?.id || tipo?._id || espelho?.id || espelho?._id;
      const tipoCatalogo = this.tiposDeEquipamento.find((registro) => {
        const id = registro?.id || registro?._id;
        return id && tipoId && String(id).toLowerCase() === String(tipoId).toLowerCase();
      });
      const siglaCatalogo = tipoCatalogo?.sigla || tipoCatalogo?.codigo || tipoCatalogo?.code;
      if (siglaCatalogo) return String(siglaCatalogo).trim().toUpperCase();

      // Fallback: extrai a sigla a partir do prefixo do código da proposta (ex.: "DED-003020-RV03" -> "DED"),
      // já que o código sempre nasce com a sigla do Tipo de Equipamento escolhido na abertura.
      const codigo = String(dados?.codigo || item?.codigo || "").trim();
      const matchPrefixo = codigo.match(/^([A-Za-z]{2,6})-/);
      return matchPrefixo ? matchPrefixo[1].toUpperCase() : "";
    },
    obterLabelTipoEquipamentoSelecionado() {
      const opcao = this.opcoesTipoEquipamento.find((item) => item.value === this.filtros.tipoEquipamento);
      return opcao ? opcao.text : this.filtros.tipoEquipamento;
    },
    async buscarDados() {
      this.loading = true;
      try {
        const [response, tiposResponse] = await Promise.all([
          Vue.prototype.$http.post("/View/run", {
          params: [
            {
              dataType: "String",
              name: "dataInicio",
              value: this.filtros.dataInicio
                ? moment(this.filtros.dataInicio).format("YYYY-MM-DD")
                : moment("1970-01-01").format("YYYY-MM-DD")
            },
            {
              dataType: "String",
              name: "dataFim",
              value: this.filtros.dataFim
                ? moment(this.filtros.dataFim).format("YYYY-MM-DD")
                : moment().format("YYYY-MM-DD")
            },
            // A view "get-relatorio-sales-order" exige este parâmetro presente na lista (mesmo vazio) —
            // removê-lo zera o resultado da consulta no backend. Não há mais filtro de Proposta na tela
            // (substituído por Tipo de Equipamento, filtrado no cliente via tipoEquipamentoSigla), então
            // ele sempre vai vazio.
            { dataType: "string", name: "codigo", value: "" },
            { dataType: "string", name: "cliente", value: this.filtros.cliente },
            { dataType: "string", name: "vendedor", value: this.filtros.vendedor },
            { dataType: "string", name: "orcamentista", value: this.filtros.orcamentista },
            { dataType: "string", name: "order", value: "clienteNome" },
            { dataType: "Number", name: "desc", value: 1 }
          ],
          viewName: "get-relatorio-sales-order",
          page: 1,
          limit: 3000000
          }),
          this.tipoEquipamentoService
            .getAll(1, 500, "", "", null, null, null, "descricao")
            .catch(() => null)
        ]);

        const tipos = tiposResponse?.data?.data?.data || tiposResponse?.data?.data || tiposResponse?.data || [];
        this.tiposDeEquipamento = Array.isArray(tipos) ? tipos : [];

        const listaBruta = response?.data?.data?.data || [];
        this.dados = listaBruta.map((item = {}) => {
          const dados = item?.revisoes || item || {};

          const precoComIcmsIpi =
            toNumber(dados?.precoVenda_cIPI_cICMS) ||
            toNumber(dados?.precoVenda_sIPI_cICMS) ||
            toNumber(dados?.valorEstimado) ||
            0;

          return {
            _id: dados?._id || dados?.id || item?._id || item?.id || Math.random().toString(36).slice(2),
            codigo: dados?.codigo || "",
            clienteNome: dados?.clienteNome || "Cliente Não Informado",
            projetoProduto: dados?.projetoDescricao || dados?.descricao || "-",
            dataSolicitacao: dados?.dateGenerate || dados?.dataSolicitacao || dados?.dataCriacao || null,
            precoComIcmsIpi,
            status: dados?.statusDescription || dados?.status || "-",
            vendedorNome: dados?.vendedorNome || "",
            orcamentistaNome: dados?.orcamentistaNome || "",
            contatoNome: dados?.contatoComercialNome || dados?.contatoNome || "",
            contatoTelefone: dados?.contatoComercialTelefone || dados?.contatoTelefone || "",
            dataConclusao: dados?.dataConclusao || dados?.dataDesejada || dados?.dataDespacho || null,
            prazoResposta: dados?.prazoResposta || dados?.dataDesejada || dados?.dataDespacho || null,
            tipoEquipamento: this.obterDescricaoTipoEquipamento(dados, item),
            tipoEquipamentoSigla: this.obterSiglaTipoEquipamento(dados, item),
            observacaoTecnica: dados?.observacaoTecnica || dados?.observacao || ""
          };
        });
      } finally {
        this.loading = false;
      }
    },
    // WORKAROUND: esta função é a ÚNICA fonte confiável do resumo de filtros exibido no cabeçalho
    // do PDF/Excel exportado (contexto.subtitulos, montado em resolverContextoExportacao). Antes,
    // cada ramo de resolverContextoExportacao montava seu próprio subtítulo manualmente e citava
    // só 1 ou 2 filtros (ex.: só "Cliente: X" ou só "Situação em {hoje}") — qualquer outro filtro
    // ativo ao mesmo tempo (Tipo de Equipamento, período, Vendedor, Orçamentista etc.) ficava de
    // fora do PDF mesmo estando aplicado na tela, e quando o Status não batia com nenhuma palavra-
    // chave reconhecida (ex. "CANCELADO"), o cabeçalho voltava a exibir o texto genérico
    // "Classificação: Orçamento", como se nenhum filtro estivesse selecionado. Esta função lista
    // SOMENTE os filtros de fato ativos (omite os que estão em "Todos"), então o cabeçalho
    // exportado sempre reflete exatamente o que está selecionado na tela, seja qual for a
    // combinação de filtros.
    obterFiltrosDescricao() {
      const linhas = [];

      if (this.filtros.dataInicio || this.filtros.dataFim) {
        const periodoInicio = this.filtros.dataInicio ? this.formatarDataPtBr(this.filtros.dataInicio) : "-";
        const periodoFim = this.filtros.dataFim ? this.formatarDataPtBr(this.filtros.dataFim) : "-";
        linhas.push(`Período: ${periodoInicio} a ${periodoFim}`);
      }

      if (this.filtros.tipoEquipamento) {
        linhas.push(`Tipo de Equipamento: ${this.obterLabelTipoEquipamentoSelecionado()}`);
      }
      if (this.filtros.cliente) linhas.push(`Cliente: ${this.filtros.cliente}`);
      if (this.filtros.vendedor) linhas.push(`Vendedor: ${this.filtros.vendedor}`);
      if (this.filtros.orcamentista) linhas.push(`Orçamentista: ${this.filtros.orcamentista}`);
      if (this.filtros.status) linhas.push(`Status: ${this.filtros.status}`);

      const sortBy = Array.isArray(this.tableOptions?.sortBy) ? this.tableOptions.sortBy[0] : "";
      const sortDesc = Array.isArray(this.tableOptions?.sortDesc) ? Boolean(this.tableOptions.sortDesc[0]) : false;
      const headerSort = this.headers.find((header) => header.value === sortBy);
      const descricaoOrdenacao = headerSort
        ? `${headerSort.text} (${sortDesc ? "Decrescente" : "Crescente"})`
        : "Padrão";
      linhas.push(`Ordenação: ${descricaoOrdenacao}`);

      if (linhas.length === 1) linhas.unshift("Geral (sem filtros aplicados)");

      return linhas;
    },
    obterResumoPdf() {
      const linhas = this.linhasExportacao;
      const totalQuantidade = linhas.length;
      const totalValor = linhas.reduce((acc, item) => acc + Number(item?.precoComIcmsIpi || 0), 0);

      if (this.filtros.status) {
        return [
          `Total de Propostas: ${totalQuantidade}`,
          `Total em Valor: ${this.formatarMoedaPtBr(totalValor)}`
        ];
      }

      const porStatus = linhas.reduce((acc, item) => {
        const status = normalizarTexto(item?.status) || "Sem Status";
        if (!acc[status]) {
          acc[status] = { quantidade: 0, valor: 0 };
        }
        acc[status].quantidade += 1;
        acc[status].valor += Number(item?.precoComIcmsIpi || 0);
        return acc;
      }, {});

      const resumo = [
        `Total de Propostas: ${totalQuantidade}`,
        `Total em Valor: ${this.formatarMoedaPtBr(totalValor)}`
      ];

      Object.entries(porStatus)
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([status, valores]) => {
          resumo.push(`${status}: ${valores.quantidade} proposta(s) - ${this.formatarMoedaPtBr(valores.valor)}`);
        });

      return resumo;
    },
    obterPeriodoDescricao() {
      if (!this.filtros.dataInicio && !this.filtros.dataFim) return "Geral";

      const inicio = this.filtros.dataInicio ? this.formatarDataPtBr(this.filtros.dataInicio) : "início";
      const fim = this.filtros.dataFim ? this.formatarDataPtBr(this.filtros.dataFim) : "hoje";
      return `De ${inicio} até ${fim}`;
    },
    resolverContextoExportacao() {
      const status = normalizarChave(this.filtros.status);
      const porCliente = Boolean(this.filtros.cliente);
      const porVendedor = Boolean(this.filtros.vendedor);
      const porOrcamentista = Boolean(this.filtros.orcamentista);

      const colunas = {
        codigo: { text: "Orçamento", value: "codigo", largura: 16 },
        cliente: { text: "Cliente", value: "clienteNome", largura: 30 },
        projeto: { text: "Projeto", value: "projetoProduto", largura: 42 },
        contato: { text: "Contato", value: "contatoNome", largura: 28 },
        telefone: { text: "Fone", value: "contatoTelefone", largura: 18 },
        solicitacao: { text: "Dt. Solic.", value: "dataSolicitacao", largura: 14 },
        conclusao: { text: "Dt. Concl.", value: "dataConclusao", largura: 14 },
        prazo: { text: "Prazo", value: "prazoResposta", largura: 14 },
        vendedor: { text: "Vendedor", value: "vendedorNome", largura: 28 },
        orcamentista: { text: "Orçamentista", value: "orcamentistaNome", largura: 28 },
        equipe: { text: "Vend./Orç.", value: "equipe", largura: 22 },
        valor: { text: "Valor Orç.", value: "precoComIcmsIpi", largura: 18 },
        equipamento: { text: "Tipo de Equipamento", value: "tipoEquipamento", largura: 44 },
        observacao: { text: "Observação Técnica", value: "observacaoTecnica", largura: 38 },
        status: { text: "Status", value: "status", largura: 16 }
      };

      const contexto = {
        titulo: "Relatório de Orçamentos - Geral",
        headers: [colunas.codigo, colunas.cliente, colunas.projeto, colunas.conclusao, colunas.valor, colunas.status],
        nomeBase: "relatorio-orcamentos"
      };

      if (status.includes("ANALISE")) {
        contexto.titulo = "Propostas em análise (pelo cliente)";
        contexto.headers = [colunas.codigo, colunas.cliente, colunas.projeto, colunas.contato, colunas.telefone, colunas.solicitacao, colunas.equipe, colunas.valor];
        contexto.nomeBase = "propostas-em-analise";

        if (porCliente) {
          contexto.headers = [colunas.codigo, colunas.solicitacao, colunas.conclusao, colunas.equipamento, colunas.valor];
        } else if (porVendedor) {
          contexto.headers = [colunas.codigo, colunas.cliente, colunas.contato, colunas.telefone, colunas.solicitacao, colunas.conclusao, colunas.equipamento, colunas.valor];
        }
      } else if (status.includes("EXECUCAO")) {
        contexto.titulo = "Propostas em Execução - Geral";
        contexto.headers = [colunas.codigo, colunas.cliente, colunas.projeto, colunas.contato, colunas.telefone, colunas.solicitacao, colunas.prazo, colunas.equipe, colunas.observacao];
        contexto.nomeBase = "propostas-em-execucao";

        if (porCliente) {
          contexto.titulo = "Propostas em Execução";
          contexto.headers = [colunas.codigo, colunas.cliente, colunas.solicitacao, colunas.prazo, colunas.orcamentista, colunas.equipamento];
        } else if (porOrcamentista) {
          contexto.titulo = "Propostas em Execução por Orçamentista";
          contexto.headers = [colunas.codigo, colunas.cliente, colunas.equipamento, colunas.contato, colunas.telefone, colunas.solicitacao, colunas.prazo, colunas.vendedor];
        } else if (porVendedor) {
          contexto.titulo = "Propostas em Execução - Vendedor";
        }
      } else if (status.includes("APROV") || status.includes("PEDIDO COLOCADO")) {
        contexto.titulo = "Propostas Aprovadas";
        contexto.headers = porCliente || porVendedor
          ? [colunas.codigo, ...(porVendedor ? [colunas.cliente] : []), colunas.solicitacao, colunas.conclusao, colunas.equipamento, colunas.valor]
          : [colunas.codigo, colunas.cliente, colunas.solicitacao, colunas.conclusao, colunas.vendedor, colunas.valor, colunas.equipamento];
        contexto.nomeBase = "propostas-aprovadas";
      } else if (status.includes("PERD")) {
        contexto.titulo = "Propostas Perdidas";
        contexto.headers = porCliente
          ? [colunas.codigo, colunas.solicitacao, colunas.conclusao, colunas.equipamento, colunas.valor]
          : [colunas.codigo, colunas.cliente, colunas.solicitacao, colunas.conclusao, colunas.vendedor, colunas.valor, colunas.equipamento];
        contexto.nomeBase = "propostas-perdidas";
      } else if (porCliente || porVendedor || porOrcamentista || this.filtros.tipoEquipamento) {
        const classificacao = porCliente
          ? `Do Cliente: ${this.filtros.cliente}`
          : porVendedor
            ? `Do Vendedor: ${this.filtros.vendedor}`
            : porOrcamentista
              ? `Do Orçamentista: ${this.filtros.orcamentista}`
              : `Do Tipo de Equipamento: ${this.obterLabelTipoEquipamentoSelecionado()}`;
        contexto.titulo = `Relatório de Orçamentos - ${classificacao}`;
      }

      // O título acima só serve para dar um nome amigável ao relatório (baseado no Status
      // predominante ou na classificação principal); o resumo de filtros exibido logo abaixo dele
      // no PDF/Excel vem sempre de obterFiltrosDescricao(), que é a única fonte que garante listar
      // TODOS os filtros realmente ativos na tela (ver comentário no método).
      contexto.subtitulos = this.obterFiltrosDescricao();

      return {
        ...contexto,
        rows: this.linhasExportacao.map((item) => ({
          ...item,
          equipe: [item.vendedorNome, item.orcamentistaNome].filter(Boolean).join(" / ")
        })),
        formatadores: {
          dataSolicitacao: (valor) => this.formatarDataPtBr(valor),
          dataConclusao: (valor) => this.formatarDataPtBr(valor),
          prazoResposta: (valor) => this.formatarDataPtBr(valor),
          precoComIcmsIpi: (valor) => this.formatarMoedaPtBr(valor)
        }
      };
    },
    async exportarPdfDetalhado() {
      const contexto = this.resolverContextoExportacao();

      await exportarPdfTabela({
        headers: contexto.headers,
        rows: contexto.rows,
        nomeArquivo: `${contexto.nomeBase}.pdf`,
        tituloRelatorio: contexto.titulo,
        logoNoLadoEsquerdo: true,
        tituloNoLadoDireito: true,
        subtituloRelatorioLinhas: contexto.subtitulos,
        resumoFinalLinhas: this.obterResumoPdf(),
        formatadoresPorCampo: contexto.formatadores
      });
    },
    exportarExcelDetalhado() {
      const contexto = this.resolverContextoExportacao();
      const quantidadeColunas = contexto.headers.length;
      const linhasCabecalho = [
        ["DELTA DUCON", contexto.titulo],
        ...contexto.subtitulos.map((subtitulo) => ["", subtitulo]),
        []
      ];
      const linhasTabela = contexto.rows.map((item) => contexto.headers.map((header) => {
        const valor = item?.[header.value];
        if (header.value === "precoComIcmsIpi") return Number(valor || 0);
        const formatador = contexto.formatadores[header.value];
        return typeof formatador === "function" ? formatador(valor, item) : (valor || "");
      }));

      const sheet = XLSX.utils.aoa_to_sheet(linhasCabecalho);
      XLSX.utils.sheet_add_aoa(sheet, [contexto.headers.map((header) => header.text), ...linhasTabela], {
        origin: `A${linhasCabecalho.length + 1}`
      });

      sheet["!cols"] = contexto.headers.map((header) => ({ wch: header.largura || 20 }));
      sheet["!merges"] = [XLSX.utils.decode_range(`B1:${XLSX.utils.encode_col(Math.max(1, quantidadeColunas - 1))}1`)];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, "Relatório");
      XLSX.writeFile(workbook, `${contexto.nomeBase}.xlsx`);
    }
  },
  async mounted() {
    if (this.relatorioTemPermissao(this.relatorioSchemas)) {
      await this.buscarDados();
    }
  }
};
</script>

<style scoped>
.c-filtro {
  min-width: 200px;
  max-width: 260px;
}
</style>
